import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../hooks/useAuth';
import { newListingStatus, useListingActions, useMyItems } from '../hooks/useListingAdmin';
import { CategoryKey, Contact, Listing, ListingLocation, ShiurWhere } from '../types';
import { CATEGORIES, getCategory } from '../config/categories';
import { DEFAULT_AREA_ID } from '../config/areas';
import { checkListingFields, fillFromListing, LISTING_LIMITS, newId } from '../utils/listingEdit';
import { pickLang } from '../utils/listingDisplay';
import LazyPinPickerMap from '../components/directory/map/LazyPinPickerMap';
import AddressSearch from '../components/admin/listing/AddressSearch';
import ForgotPassword from '../components/auth/ForgotPassword';
import CategoryIcon from '../components/directory/CategoryIcon';
import { useLinkableShuls } from '../components/admin/listing/ShiurDetailsFields';
import {
  BilingualField,
  Checkbox,
  ErrorList,
  SectionTitle,
  SelectField,
  TextField,
} from '../components/admin/listing/formFields';

type AccountMode = 'new' | 'existing';

/** A copied location without its map point (the form's pin decides that). */
function withoutPoint(loc: ListingLocation | undefined): Partial<ListingLocation> {
  if (!loc) return {};
  const { lat, lng, ...rest } = loc;
  return rest;
}

function authErrorKey(err: unknown): string {
  const code = (err as { code?: string } | undefined)?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'admin.signupEmailInUse';
  if (code === 'auth/weak-password') return 'admin.signupWeakPassword';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'admin.loginError';
  }
  if (code === 'permission-denied') return 'directory.admin.add.notAllowed';
  return 'directory.admin.add.failed';
}

/**
 * "Add a listing" (plan 10.5): anyone signed in can submit; it waits as
 * 'pending' until the super admin approves. A short form; everything else is
 * filled in afterwards under My Items.
 */
export default function AddListingPage() {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  usePageTitle(t('seo.add'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, signIn, signUp, ensureOwnerProfile } = useAuth();
  const { createListing, saving } = useListingActions();

  const initialCategory = CATEGORIES.find((c) => c.key === searchParams.get('category'))?.key ?? 'gemach';
  const [category, setCategory] = useState<CategoryKey>(initialCategory);
  const [subcategory, setSubcategory] = useState(getCategory(initialCategory).subcategories[0] ?? '');
  const [nameHe, setNameHe] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [addressHe, setAddressHe] = useState('');
  const [addressEn, setAddressEn] = useState('');
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [showExactLocation, setShowExactLocation] = useState(true);
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [descriptionHe, setDescriptionHe] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');

  // Shiurim (docs/shiurim-plan.md): where it is held. ?shul=<id> comes from "Add a shiur in this shul".
  const shulParam = searchParams.get('shul') ?? '';
  const [where, setWhere] = useState<ShiurWhere>(shulParam ? 'shul' : 'place');
  const [shulId, setShulId] = useState(shulParam);
  const [giverHe, setGiverHe] = useState('');
  const [giverEn, setGiverEn] = useState('');
  const [topicOtherHe, setTopicOtherHe] = useState('');
  const [topicOtherEn, setTopicOtherEn] = useState('');
  const { shuls: linkableShuls } = useLinkableShuls(shulParam || undefined);
  const isShiur = category === 'shiur';
  const shiurInShul = isShiur && where === 'shul';
  const linkedShul = linkableShuls.find((l) => l.id === shulId);
  const needsAddress = !isShiur || where === 'place';

  const [accountMode, setAccountMode] = useState<AccountMode>('new');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // "Fill in from my listing" (plan 16): the rest of the source's location and contacts, kept for saving.
  const { items: myItems } = useMyItems(user?.uid, isHebrew);
  const [fillFromId, setFillFromId] = useState('');
  const [filled, setFilled] = useState<{ location: ListingLocation; contacts: Contact[] } | null>(null);

  const chooseFillFrom = (id: string) => {
    setFillFromId(id);
    const source = myItems.find((l) => l.id === id);
    const fill = source ? fillFromListing(source) : null;
    setFilled(fill);
    const first = fill?.contacts[0];
    setAddressHe(fill?.location.addressHe ?? '');
    setAddressEn(fill?.location.addressEn ?? '');
    const loc = fill?.location;
    setPoint(loc && loc.lat !== undefined && loc.lng !== undefined ? { lat: loc.lat, lng: loc.lng } : null);
    setShowExactLocation(fill?.location.showExactLocation ?? true);
    setContactName(first ? pickLang(first.nameEn, first.nameHe, isHebrew) : '');
    setPhone(first?.phone ?? '');
  };

  // ?from=<id> (the "Add another listing with these details" link): pick it once the user's listings load.
  const fromParam = searchParams.get('from') ?? '';
  useEffect(() => {
    if (fromParam && !fillFromId && myItems.some((l: Listing) => l.id === fromParam)) chooseFillFrom(fromParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromParam, myItems]);

  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const subcategories = getCategory(category).subcategories;

  const chooseCategory = (key: CategoryKey) => {
    setCategory(key);
    setSubcategory(getCategory(key).subcategories[0] ?? '');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    const problems: string[] = checkListingFields({ category, subcategory, nameHe, nameEn, descriptionHe, descriptionEn });
    if (!consent) problems.push('consentRequired');
    if (shiurInShul && !linkedShul) problems.push('shulRequired');
    setErrors(problems);
    if (problems.length) return;

    setBusy(true);
    try {
      if (!user) {
        if (accountMode === 'new') await signUp(email, password, displayName || contactName);
        else await signIn(email, password);
      }
      await ensureOwnerProfile(displayName || contactName);
      const [firstFilled, ...otherFilled] = filled?.contacts ?? [];
      const firstName = firstFilled ? pickLang(firstFilled.nameEn, firstFilled.nameHe, isHebrew) : '';
      let contact: Contact = { id: newId(), nameHe: contactName, phone };
      if (firstFilled) {
        // Keep the copied WhatsApp / email; the name field replaces the name only when it was changed.
        const { nameEn, nameHe, ...rest } = firstFilled;
        const names = contactName === firstName ? { nameHe, nameEn } : { nameHe: contactName };
        contact = { ...rest, ...names, phone };
      }
      const typedLocation: Partial<ListingLocation> = {
        ...withoutPoint(filled?.location),
        addressHe,
        addressEn,
        ...(point ?? {}),
        areaId: filled?.location.areaId ?? DEFAULT_AREA_ID,
        showExactLocation,
      };
      // A shiur in a shul takes the shul's address and pin; an online one has none.
      const location: Partial<ListingLocation> = !isShiur
        ? typedLocation
        : where === 'shul' && linkedShul
          ? { ...linkedShul.location }
          : where === 'online'
            ? { areaId: DEFAULT_AREA_ID, addressHe: '', addressEn: '', showExactLocation: true }
            : typedLocation;
      const shiurDetails = {
        where,
        ...(where === 'shul' ? { shulId } : {}),
        ...(giverHe.trim() ? { giverHe: giverHe.trim() } : {}),
        ...(giverEn.trim() ? { giverEn: giverEn.trim() } : {}),
        ...(subcategory === 'other' && topicOtherHe.trim() ? { topicOtherHe: topicOtherHe.trim() } : {}),
        ...(subcategory === 'other' && topicOtherEn.trim() ? { topicOtherEn: topicOtherEn.trim() } : {}),
      };
      const id = await createListing(
        {
          category,
          subcategory: category === 'shul' ? undefined : subcategory,
          nameHe,
          nameEn,
          descriptionHe,
          descriptionEn,
          location,
          ...(isShiur ? { details: shiurDetails } : {}),
          contacts: [contact, ...otherFilled],
          publishConsentAt: new Date().toISOString(),
        },
        newListingStatus(user)
      );
      navigate(`/admin?item=${id}&added=1`, { replace: true });
    } catch (err) {
      setMessage(t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="max-w-2xl mx-auto px-4 py-10 text-sm text-ash">{t('common.loading')}</p>;

  if (user?.status === 'rejected') {
    return <p className="max-w-2xl mx-auto px-4 py-10 text-sm text-ink">{t('directory.admin.add.notAllowed')}</p>;
  }

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-ink">{t('directory.admin.addListing')}</h1>
        <p className="text-sm text-ash">{t('directory.admin.add.intro')}</p>
      </div>

      {myItems.length > 0 && (
        <section className="space-y-2 rounded-[16px] bg-fog p-4">
          <SelectField
            label={t('directory.admin.fillFrom.label')}
            value={fillFromId}
            onChange={chooseFillFrom}
            options={[
              { value: '', label: t('directory.admin.fillFrom.none') },
              ...myItems.map((l) => ({ value: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) })),
            ]}
          />
          <p className="text-xs text-ash">{t('directory.admin.fillFrom.hint')}</p>
        </section>
      )}

      <section className="space-y-4">
        <SectionTitle level={2}>{t('directory.field.category')}</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={category === c.key}
              onClick={() => chooseCategory(c.key)}
              className={`flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-[10px] border text-sm text-start ${
                category === c.key ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
              }`}
            >
              <CategoryIcon category={c.key} className="w-4 h-4 shrink-0" />
              {t(`directory.category.${c.key}`)}
            </button>
          ))}
        </div>
        {subcategories.length > 0 && (
          <SelectField
            label={t('directory.field.subcategory')}
            info={t('directory.admin.info.subcategoryHint')}
            value={subcategory}
            onChange={setSubcategory}
            options={subcategories.map((s) => ({ value: s, label: t(`directory.sub.${s}`) }))}
          />
        )}
      </section>

      <section className="space-y-4">
        <SectionTitle level={2}>{t('directory.admin.add.whatTitle')}</SectionTitle>
        <BilingualField
          labelHe={t('directory.field.nameHe')}
          labelEn={t('directory.field.nameEn')}
          valueHe={nameHe}
          valueEn={nameEn}
          onChangeHe={setNameHe}
          onChangeEn={setNameEn}
          maxLength={LISTING_LIMITS.nameLength}
          info={t('directory.admin.info.namesHint')}
        />
        <BilingualField
          labelHe={t('directory.field.descriptionHe')}
          labelEn={t('directory.field.descriptionEn')}
          valueHe={descriptionHe}
          valueEn={descriptionEn}
          onChangeHe={setDescriptionHe}
          onChangeEn={setDescriptionEn}
          multiline
          maxLength={LISTING_LIMITS.textLength}
        />
      </section>

      {isShiur && (
        <section className="space-y-4">
          <SectionTitle level={2}>{t('directory.category.shiur')}</SectionTitle>
          {subcategory === 'other' && (
            <BilingualField
              labelHe={t('directory.shiur.topicOtherHe')}
              labelEn={t('directory.shiur.topicOtherEn')}
              valueHe={topicOtherHe}
              valueEn={topicOtherEn}
              onChangeHe={setTopicOtherHe}
              onChangeEn={setTopicOtherEn}
              maxLength={100}
            />
          )}
          <BilingualField
            labelHe={t('directory.shiur.giverHe')}
            labelEn={t('directory.shiur.giverEn')}
            valueHe={giverHe}
            valueEn={giverEn}
            onChangeHe={setGiverHe}
            onChangeEn={setGiverEn}
          />
          <SelectField<ShiurWhere>
            label={t('directory.shiur.where')}
            value={where}
            onChange={setWhere}
            options={(['shul', 'place', 'online'] as ShiurWhere[]).map((w) => ({
              value: w,
              label: t(`directory.shiur.whereOption.${w}`),
            }))}
          />
          {where === 'shul' &&
            (linkableShuls.length === 0 ? (
              <p className="text-sm text-ink">{t('directory.shiur.noShuls')}</p>
            ) : (
              <SelectField
                label={t('directory.shiur.shul')}
                info={t('directory.shiur.shulHint')}
                value={shulId}
                onChange={setShulId}
                options={[
                  { value: '', label: t('directory.shiur.pickShul') },
                  ...linkableShuls.map((l) => ({ value: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) })),
                ]}
              />
            ))}
          {where !== 'place' && (
            <p className="text-xs text-ash">
              {t(where === 'shul' ? 'directory.shiur.locationFromShul' : 'directory.shiur.locationOnline')}
            </p>
          )}
        </section>
      )}

      {needsAddress && (
        <section className="space-y-4">
          <SectionTitle level={2}>{t('directory.admin.tab.location')}</SectionTitle>
          <AddressSearch
            areaId={filled?.location.areaId}
            onPick={(p) => {
              setAddressHe(p.addressHe);
              setAddressEn(p.addressEn);
              setPoint({ lat: p.lat, lng: p.lng });
            }}
          />
          <BilingualField
            labelHe={t('directory.field.addressHe')}
            labelEn={t('directory.field.addressEn')}
            valueHe={addressHe}
            valueEn={addressEn}
            onChangeHe={setAddressHe}
            onChangeEn={setAddressEn}
            maxLength={LISTING_LIMITS.addressLength}
            placeholderHe={t('directory.admin.location.addressPlaceholderHe')}
            placeholderEn={t('directory.admin.location.addressPlaceholderEn')}
          />
          <p className="text-sm text-ash">{t('directory.admin.location.mapHint')}</p>
          <LazyPinPickerMap value={point} onChange={setPoint} category={category} approximate={!showExactLocation} />
          <Checkbox
            label={t('directory.field.showExactLocation')}
            checked={showExactLocation}
            onChange={setShowExactLocation}
            hint={
              showExactLocation ? t('directory.admin.location.exactOnHint') : t('directory.admin.location.exactOffHint')
            }
          />
        </section>
      )}

      <section className="space-y-4">
        <SectionTitle level={2}>{t('directory.admin.add.contactTitle')}</SectionTitle>
        <p className="text-sm text-ash">{t('directory.admin.contacts.hint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label={t('directory.field.contactName')} value={contactName} onChange={setContactName} />
          <TextField
            label={t('directory.field.phone')}
            type="tel"
            dir="ltr"
            value={phone}
            onChange={setPhone}
            placeholder="050-1234567"
          />
        </div>
      </section>

      {!user && (
        <section className="space-y-4 rounded-[16px] border border-ink/10 p-4">
          <SectionTitle level={2}>{t('directory.admin.add.accountTitle')}</SectionTitle>
          <p className="text-sm text-ash">{t('directory.admin.add.accountHint')}</p>
          <div className="flex rounded-[10px] border border-ink/15 p-0.5">
            {(['new', 'existing'] as AccountMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setAccountMode(mode)}
                className={`flex-1 min-h-[44px] rounded-[10px] text-sm font-medium ${
                  accountMode === mode ? 'bg-ink text-paper' : 'text-ink'
                }`}
              >
                {t(`directory.admin.add.account.${mode}`)}
              </button>
            ))}
          </div>
          {accountMode === 'new' && (
            <TextField label={t('admin.contactName')} value={displayName} onChange={setDisplayName} />
          )}
          <TextField label={t('admin.email')} type="email" dir="ltr" value={email} onChange={setEmail} required />
          <div>
            <label htmlFor="add-password" className="block text-sm font-medium text-ink mb-1">
              {t('admin.password')}
            </label>
            <input
              id="add-password"
              type="password"
              required
              minLength={6}
              autoComplete={accountMode === 'new' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
              dir="ltr"
            />
          </div>
          {accountMode === 'existing' && <ForgotPassword email={email} />}
        </section>
      )}

      <section className="space-y-2">
        <Checkbox label={t('privacy.consent.label')} checked={consent} onChange={setConsent} />
        <p className="text-xs text-ash">
          {t('privacy.consent.readMore')}{' '}
          <Link to="/privacy" className="underline text-ink">
            {t('privacy.title')}
          </Link>
          {' · '}
          <Link to="/terms" className="underline text-ink">
            {t('terms.title')}
          </Link>
        </p>
      </section>

      <ErrorList errors={errors} />
      {message && (
        <p role="alert" className="text-sm text-danger-600">
          {message}
        </p>
      )}

      <div className="space-y-2">
        <button
          type="submit"
          disabled={busy || saving}
          className="w-full min-h-[48px] rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {busy || saving ? t('directory.admin.saving') : t('directory.admin.add.submit')}
        </button>
        <p className="text-xs text-ash text-center">
          {user?.role === 'superAdmin' ? t('directory.admin.add.superNote') : t('directory.admin.add.approvalNote')}
        </p>
        {user && (
          <p className="text-xs text-ash text-center">
            {t('directory.admin.add.signedInAs', { email: user.email })}{' '}
            <Link to="/admin" className="underline">
              {t('directory.admin.myItems')}
            </Link>
          </p>
        )}
      </div>
    </form>
  );
}
